import Icon from '@aurion/storybook-ui/components/Icon';
import {
    faCheck,
    faInfoCircle,
    faExclamationTriangle,
    faTimes,
    faDiamondExclamation,
    IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import { Box, Typography, IconButton } from '@mui/material';
import { CustomContentProps, SnackbarContent, useSnackbar, VariantType } from 'notistack';
import React, { forwardRef } from 'react';

/**
 * This is the custom SnackBar component which overrides Notistack's default styling.
 */
const SnackBar = forwardRef<HTMLDivElement, Partial<CustomContentProps>>((props, forwardedRef) => {
    const { message, variant, id } = props;

    const { closeSnackbar } = useSnackbar();

    const alertStyles: Record<
        Exclude<VariantType, 'default'>,
        {
            icon: IconDefinition;
            iconBackground: string;
            messageBackground: string;
            messageColor: string;
        }
    > = {
        success: {
            icon: faCheck,
            iconBackground: '#0A8A11',
            messageBackground: '#C7F4C9',
            messageColor: '#000000',
        },
        info: {
            icon: faInfoCircle,
            iconBackground: '#04A7ED',
            messageBackground: '#B5E4F9',
            messageColor: '#000000',
        },
        error: {
            icon: faDiamondExclamation,
            iconBackground: '#D32F2F',
            messageBackground: '#FFCFCF',
            messageColor: '#000000',
        },
        warning: {
            icon: faExclamationTriangle,
            iconBackground: '#FF8026',
            messageBackground: '#FFDAAE',
            messageColor: '#000000',
        },
    };

    const style = alertStyles[variant as Exclude<VariantType, 'default'>];

    return (
        <SnackbarContent ref={forwardedRef}>
            <Box
                data-testid="snackbar-container-top"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    width: '100%',
                    boxShadow: '0px 3px 6px rgba(0,0,0,0.4)',
                    backgroundColor: style.messageBackground,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: style.iconBackground,
                        color: '#FFFFFF',
                        width: '55px',
                        height: '55px',
                    }}
                >
                    <Icon icon={style.icon} />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        color: style.messageColor,
                    }}
                >
                    <Typography
                        data-testid="snackbar-message"
                        variant="body2"
                        sx={{
                            fontSize: '15px',
                            padding: '12px',
                        }}
                    >
                        {message}
                    </Typography>
                </Box>

                <IconButton
                    data-testid="snackbar-close-button"
                    onClick={() => closeSnackbar(id)}
                    sx={{
                        paddingX: '12px',
                        color: '#000000',
                    }}
                >
                    <Icon icon={faTimes} />
                </IconButton>
            </Box>
        </SnackbarContent>
    );
});

export default SnackBar;
